import { useEffect, useState } from "react";

import {
  Modal,
  Button,
  TextArea,
  Table,
  TableMetadata,
  Typography,
  useTable,
  Label,
} from "@king-ict/eupisi-ui/components";
import { hrDate } from "@king-ict/eupisi-ui/utils";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router";

import { DEFAULT_INSTITUTION_COMMITTEE } from "./constants";
import { generateCommitteeTableColumns, addRowKeyIdToTableData } from "./utils";
import {
  getInstitutionCommitteeById,
  changeInstitutionCommitteeStatus,
} from "../../api/Committee/InstitutionCommitteeApi";
import { getSchoolYearCurrent } from "../../api/SchoolYear/SchoolYearApi";
import {
  ToolbarEdit,
  ToolbarTop,
  FormCard,
  ContentArea,
} from "../../components";
import { useEnumCodeBooks } from "../../hooks";
import {
  CommitteeStatus,
  CommitteeEmployee,
  InstitutionCommittee,
} from "../../models/Committee";
import { ImportantDateEventEnum } from "../../models/ImportantDate/ImportantDateEventEnum";
import {
  committeeApprovalEditUrl,
  committeeApprovalListRoute,
} from "../../routes/routes";
import { FormatUtils } from "../../utils";

export function CommitteeApprovalDetails() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const history = useHistory();

  const [item, setItem] = useState<InstitutionCommittee>(
    DEFAULT_INSTITUTION_COMMITTEE
  );
  const [tableData, setTableData] = useState<CommitteeEmployee[]>([]);

  const tableMetadata: TableMetadata<CommitteeEmployee> = {
    rowKey: "rowKeyId",
    columns: generateCommitteeTableColumns(t),
    rowToolTip: () => undefined,
  };
  const [tableProps] = useTable({
    metadata: tableMetadata,
    data: tableData,
    pagination: false,
    paginationPage: undefined,
    paginationPageSize: undefined,
    totalItems: 1,
    defaultSort: {},
  });
  const [showSaveModal, setShowSaveModal] = useState(false);
  const { committeeStatusCodeBook: statuses } = useEnumCodeBooks();
  const [isEditable, setIsEditable] = useState(false);

  useEffect(() => {
    (async () => {
      const schoolYear = await getSchoolYearCurrent();
      if (schoolYear) {
        const importantDate = schoolYear.importantDates.find(
          (val) =>
            val.eventId === ImportantDateEventEnum.InstitutionAdministration
        );
        if (importantDate) {
          const exactlyNow = new Date(Date.now());
          const dateFrom = new Date(importantDate.dateTimeFrom);
          const dateTo = new Date(importantDate.dateTimeTo);
          if (dateFrom <= exactlyNow && dateTo >= exactlyNow) {
            setIsEditable(true);
          }
        }
      }
      if (!isNaN(+id) && +id > 0) {
        const result = await getInstitutionCommitteeById(+id);
        if (result) {
          setItem(result);
          setTableData(addRowKeyIdToTableData(result));
        }
      }
    })();
  }, [id]);

  const handleApprovedClicked = async () => {
    const idNumber = Number.parseInt(id);
    const status = CommitteeStatus.Granted;
    const result = await changeInstitutionCommitteeStatus(idNumber, status);
    if (result && result.isValid) {
      setItem(result.result!);
    }
  };
  const handleRejectedClicked = async () => {
    const idNumber = Number.parseInt(id);
    const status = CommitteeStatus.Rejected;
    const result = await changeInstitutionCommitteeStatus(idNumber, status);
    if (result && result.isValid) {
      setItem(result.result!);
    }
  };
  const goBack = () => {
    history.push(committeeApprovalListRoute);
  };
  const handleEditClicked = () => {
    history.push(committeeApprovalEditUrl(+id));
  };
  return (
    <ContentArea>
      <ToolbarTop
        showAdd={"hide"}
        showBack={"show"}
        showEdit={isEditable ? "show" : "disable"}
        pageTitle={t("common.expertCommission.pageNameEdit")}
        onClickBack={goBack}
        onClickEdit={handleEditClicked}
      />
      <FormCard>
        <Row>
          <Col md={2}>
            <Typography variant="base" color="light">
              {t("common.expertCommission.institution")}
            </Typography>
          </Col>
          <Col md={10}>
            <Typography variant="base" color="light">
              {item.institution?.code} - {item.institution?.name}
            </Typography>
          </Col>
        </Row>
        <Row>
          <Col md={2}>
            <Typography variant="base" color="light">
              {t("common.expertCommission.dateFromDateTo")}
            </Typography>
          </Col>
          <Col md={4}>
            <Typography variant="base" color="light">
              {item.dateFrom ? hrDate(new Date(item.dateFrom)) : ""} -{" "}
              {item.dateTo ? hrDate(new Date(item.dateTo)) : ""}
            </Typography>
          </Col>
          <Col md={2}>
            <Typography variant="base" color="light">
              {t("common.status")}
            </Typography>
          </Col>
          <Col md={4}>
            <Label
              color={FormatUtils.getCommitteeStatusColor(item.statusId)}
              className="text-center"
              outline={false}
              fullWidth={false}
            >
              {statuses.find((v) => v.id === item.statusId)?.name}
            </Label>
          </Col>
        </Row>
      </FormCard>
      <ToolbarEdit showSave="hide" showCustom={true}>
        <Button
          className="mr-1 float-right"
          color={
            item.statusId === CommitteeStatus.NotDefined ||
            !item.dateTo ||
            !item.dateFrom
              ? undefined
              : "success"
          }
          outline={true}
          size="sm"
          onClick={handleApprovedClicked}
          disabled={
            item.statusId === CommitteeStatus.NotDefined ||
            !item.dateTo ||
            !item.dateFrom ||
            !isEditable
          }
        >
          {t("common.expertCommission.approved")}
        </Button>
        <Button
          className="mr-1 float-right"
          color={
            item.statusId === CommitteeStatus.NotDefined ||
            !item.dateTo ||
            !item.dateFrom
              ? undefined
              : "danger"
          }
          outline={true}
          size="sm"
          onClick={handleRejectedClicked}
          disabled={
            item.statusId === CommitteeStatus.NotDefined ||
            !item.dateTo ||
            !item.dateFrom ||
            !isEditable
          }
        >
          {t("common.expertCommission.rejected")}
        </Button>
      </ToolbarEdit>

      <FormCard>
        <Table {...tableProps} />
      </FormCard>

      <FormCard>
        <Row>
          <Col md={12}>
            <TextArea
              ariaLabel="item.note"
              label={t("common.expertCommission.note")}
              name={"item.note"}
              value={item.note ?? ""}
              maxLength={5000}
              disabled
            />
          </Col>
        </Row>
      </FormCard>
      <Modal
        type={"message"}
        confirmText={t("common.close")}
        open={showSaveModal}
        onConfirm={() => setShowSaveModal(false)}
        onClose={() => setShowSaveModal(false)}
      >
        {t("common.saveSuccess")}
      </Modal>
    </ContentArea>
  );
}
